// Note: This is exported but not injectable. This is on purpose as
// it's not intended to be a singleton and doesn't need to be tied
// specifically to Angular.

import { CancellationTokenSource, CancellationToken } from './cancellation-token';

interface TimeoutCallback {
  callback: () => any;
  ms: number;
}

interface StartOptions {
  throwOnStop?: boolean;
}

const delayAsync = <T>(ms: number, value?: T) => {
  return new Promise(resolve => setTimeout(() => resolve(value), ms));
};

export class AsyncWorker {
  private timeoutCallbacks: TimeoutCallback[] = [];
  private startCallback: () => any = () => {};
  private successCallback: () => any = () => {};
  private errorCallback: (error: any) => any = () => {};
  private stoppedCallback: () => any = () => {};

  private _executionState: ExecutionStates = ExecutionStates.Idle;
  get executionState(): ExecutionStates {
    return this._executionState;
  }

  //FIXME: Switch to AbortController
  private cancellationTokenSource?: CancellationTokenSource;

  constructor() {}

  async start<T>(work: (cancellationToken: CancellationToken) => Promise<T>, options?: StartOptions) {
    if (this._executionState !== ExecutionStates.Idle) {
      throw 'Cannot start already running async worker';
    }

    let defaultOptions: StartOptions = {
      throwOnStop: true,
    };
    options ||= {};
    options = Object.assign({}, defaultOptions, options);

    this._executionState = ExecutionStates.Running;
    this.cancellationTokenSource = new CancellationTokenSource();
    this.cancellationTokenSource.throwOnCancel = options?.throwOnStop ?? defaultOptions.throwOnStop ?? true;
    let cancellationToken = this.cancellationTokenSource.token;
    let timeoutTokenSource = new CancellationTokenSource();

    if (this.startCallback) this.startCallback();

    let workPromise = work(cancellationToken);
    let continuationPromise = workPromise.then(
      result => {
        timeoutTokenSource.cancel();
        this._executionState = ExecutionStates.Stopping;
        if (this.successCallback && !cancellationToken.isCancelled) {
          return this.successCallback();
        }
        return result;
      },
      error => {
        timeoutTokenSource.cancel();
        this._executionState = ExecutionStates.Stopping;
        if (this.errorCallback) {
          return this.errorCallback(error);
        } else {
          // Throw the error again since there
          // isn't  a callback to send it to
          throw error;
        }
      },
    );

    // We process the timeouts without awaiting
    let _ = this.processTimeouts(timeoutTokenSource.token);

    // When the work is done, set the state back to idle
    await continuationPromise;
    this._executionState = ExecutionStates.Idle;
  }

  private async processTimeouts(token: CancellationToken) {
    for (let timeoutCallback of this.timeoutCallbacks) {
      await delayAsync(timeoutCallback.ms);
      // The token will be cancelled when the work finishes,
      // resolve or reject. This is needed in addition to the
      // executionState check which follows. A long timeout delay
      // might mean we reach this point after the worker entered
      // idle and been started again. The token would ensure we
      // stop processing timeouts for the previous worker run.
      if (token.isCancelled) break;
      // Hack: TypeScript incorrectly assumes the _executionState
      // is always running since it doesn't see the options of
      // the promise chain as part of the execution path.
      // We can work around the problem by try-casting it to
      // tempExecutionState.
      let tempExecutionState = this._executionState as ExecutionStates;
      if (tempExecutionState != ExecutionStates.Running) break;
      if (timeoutCallback.callback) {
        let callbackResult = timeoutCallback.callback();
        let callbackPromise = Promise.resolve(callbackResult);
        await callbackPromise;
      }
    }
  }

  /**
   * Note: The stopped callback won't be reached if
   * cancellation is set to throw (which is the default).
   * This can be changed with the options passed to start.
   * */
  stop() {
    if (this._executionState !== ExecutionStates.Running) {
      return;
    }
    this.cancellationTokenSource?.cancel();
    if (this.stoppedCallback) this.stoppedCallback();
  }

  withTimeout(ms: number, callback: () => any): AsyncWorker {
    let timeoutCallback: TimeoutCallback = { ms, callback };
    this.timeoutCallbacks.push(timeoutCallback);
    return this;
  }

  onStart(callback: () => any): AsyncWorker {
    this.startCallback = callback;
    return this;
  }

  onSuccess(callback: () => any): AsyncWorker {
    this.successCallback = callback;
    return this;
  }

  onError(callback: (error: any) => any): AsyncWorker {
    this.errorCallback = callback;
    return this;
  }

  onStop(callback: () => any): AsyncWorker {
    this.stoppedCallback = callback;
    return this;
  }
}

export enum ExecutionStates {
  Idle,
  Running,
  Stopping,
}
